import { MaterialIcon } from "@website/icons/material";
import styles from "@website/ui/modal/modal.module.scss";
import { ModalPropertiesType } from "@website/ui/modal/modal.types";
import { TransitionV2 } from "@website/ui/transition";
import { deepCompare } from "@website/utils/deep-compare";
import { isSSR } from "@website/utils/is-ssr";
import {
  FC,
  MouseEvent,
  PropsWithChildren,
  memo,
  useCallback,
  useEffect
} from "react";
import { createPortal } from "react-dom";

const Modal: FC<PropsWithChildren<ModalPropertiesType>> = (properties) => {
  const {
    fullWidth,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    id,
    open,
    fullScreen,
    animate,
    children,
    title,
    onClose,
    showCloseButton,
    mobileHeight,
    className,
    closeIcon = "close",
    headerStyle,
    dialogClassName,
    iconClassName,
    titleClassName = "",
    usePortal
  } = properties;

  const stopPropagationOnModal = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (open) {
      document.body.classList.add("prevent-scroll");
      return () => document.body.classList.remove("prevent-scroll");
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const checkIfPressEsc = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          onClose?.();
          event.stopPropagation();
        }
      };
      document.addEventListener("keydown", checkIfPressEsc);
      return () => document.removeEventListener("keydown", checkIfPressEsc);
    }
  }, [onClose, open]);

  const content = (
    <TransitionV2 timeout={fullScreen ? 0 : 200} in={open}>
      {(state) => (
        <div
          className={`${styles["container"]} ${className} ${
            state === "entering" || state === "entered"
              ? styles["container-in"]
              : styles["container-out"]
          } ${fullScreen ? "!size-full rounded-none p-0 md:box-content" : ""}`}
          data-testid="modal-container"
          onClick={onClose}
        >
          <div
            role="moda"
            data-full-width={fullWidth}
            aria-labelledby={ariaLabelledby}
            aria-describedby={ariaDescribedby}
            aria-modal={true}
            onClick={stopPropagationOnModal}
            id={id}
            className={`${styles["modal"]} ${className} ${dialogClassName} ${
              styles[mobileHeight || "full"]
            } ${
              animate === "bottom"
                ? `${styles["bottom"]} ${
                    state === "entered"
                      ? styles["modal-in"]
                      : styles["modal-out"]
                  }`
                : ""
            } ${
              fullScreen ? "!size-full rounded-none p-0 md:box-content" : ""
            }`}
            tabIndex={0}
          >
            {showCloseButton || title ? (
              <div className={`${headerStyle} ${styles["header"]}`}>
                {title ? (
                  <h6
                    className={`${styles["title"]} ${titleClassName} ${
                      showCloseButton ? "max-w-[90%]" : ""
                    }`}
                  >
                    {title}
                  </h6>
                ) : (
                  ""
                )}
                {showCloseButton ? (
                  <MaterialIcon
                    className={`${styles["close"]} ${iconClassName || ""}`}
                    name={closeIcon}
                    onClick={onClose}
                  />
                ) : (
                  ""
                )}
              </div>
            ) : null}
            {children}
          </div>
        </div>
      )}
    </TransitionV2>
  );

  if (!open) return null;
  if (!usePortal) return content;
  return isSSR() ? null : createPortal(content, document.body);
};

const ModalMemoized = memo(Modal, deepCompare);
export { ModalMemoized as Modal };
